/**
 * Media-link safety — scheme guard + trust gate for externally-sourced
 * article URLs (GDELT-indexed news).
 *
 * THREAT MODEL (from the product owner):
 *   Our GDELT news-grabber (`gdelt.ts`) indexes EXTERNAL articles we did not
 *   author. A bad actor can plant a page that GDELT indexes, and we would
 *   then surface its `url` as a clickable link. Two distinct risks:
 *
 *   1. Poisoned URL SCHEME (the XSS / drive-by vector this file blocks).
 *      React escapes link TEXT, but it does NOT sanitise an href. A value
 *      like `javascript:alert(document.cookie)` placed in `<a href={...}>`
 *      will EXECUTE on click. `data:`/`vbscript:`/`blob:`/`file:` are
 *      similarly dangerous (script-bearing data URIs, legacy IE vbscript,
 *      local-file exfil). `safeArticleHref` is the hard gate against this:
 *      it returns the href ONLY for an exact `http:`/`https:` protocol and
 *      `null` for everything else.
 *
 *   2. Malicious CONTENT at an otherwise well-formed https URL (malware
 *      landing page, phishing). This file does NOT and CANNOT defend against
 *      that — see the SAFE-BROWSING TODO below. We do NOT claim to. Instead
 *      we (a) only ever open in a new tab with `rel="noopener noreferrer"`
 *      (no `window.opener` tab-nabbing, no referrer leak), and (b) flag
 *      every UNVERIFIED (T3 aggregator / unknown) source so the operator
 *      makes an informed choice rather than trusting a bare link.
 *
 * This file is the trust BOUNDARY. It must stay pure (no DOM, no fetch) so
 * it is unit-testable offline and reusable by both the React component and
 * any future ingest-time check.
 *
 * ---------------------------------------------------------------------------
 * TODO (honest next layer — NOT done here, do NOT imply it is):
 *   Scheme-validation is necessary but NOT sufficient. A perfectly-formed
 *   `https://` link can still point at malware or a phishing page. The
 *   recommended ingest-time defence is a URL-REPUTATION check before a link
 *   is ever stored/surfaced, e.g.:
 *     - Google Safe Browsing Lookup API v4 (threatMatches:find), or
 *     - abuse.ch URLhaus (https://urlhaus.abuse.ch/api/) bulk/lookup.
 *   Run it in `scripts/ingest-coverage.ts` (server-side, with the API key
 *   off the client) and persist a per-article reputation verdict on
 *   `CoverageArticle`. We deliberately do NOT stub a fake "scanned safe"
 *   badge — claiming a scan we don't perform would be worse than no badge.
 * ---------------------------------------------------------------------------
 */

import type { CoverageArticle, CredibilityTier } from './types';
import { classifyCredibility } from './gdelt';

/** Schemes we will emit into an href. EXACT match only — no substring/prefix
 *  tricks (e.g. `javascript:` cannot masquerade as `https:`). */
const ALLOWED_PROTOCOLS: ReadonlySet<string> = new Set(['http:', 'https:']);

/**
 * Return `url` as a safe href, or `null` if it is not safe to put in an
 * `<a href>`.
 *
 * Policy (deliberately strict — allowlist, not denylist):
 *   - Parse with the WHATWG `URL` constructor. Anything it cannot parse
 *     (malformed, relative without base, empty, non-string) → `null`.
 *   - The parsed `protocol` MUST be exactly `http:` or `https:`. Every other
 *     scheme — `javascript:`, `data:`, `vbscript:`, `file:`, `blob:`,
 *     `mailto:`, `tel:`, `ftp:`, `about:`, custom app schemes, etc. → `null`.
 *   - We return the URL's own normalised `href` (the parser's serialisation),
 *     not the raw input string, so there is no chance of a parsed-as-safe /
 *     used-as-different mismatch.
 *
 * An allowlist is used (not "block known-bad schemes") because the set of
 * dangerous schemes is open-ended and browser-/version-specific; only two
 * schemes are ever legitimate for a news article, so we name exactly those.
 */
export function safeArticleHref(url: unknown): string | null {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (trimmed === '') return null;

  let parsed: URL;
  try {
    // No base → relative/scheme-less inputs throw, which is what we want:
    // an article link must be an absolute http(s) URL.
    parsed = new URL(trimmed);
  } catch {
    return null;
  }

  if (!ALLOWED_PROTOCOLS.has(parsed.protocol)) return null;

  // Return the parser's own serialisation — guarantees the string we hand to
  // the href is the same one we validated.
  return parsed.href;
}

/** Trust verdict for an article's source. */
export type SourceTrust = 'trusted' | 'unverified';

export interface ArticleTrust {
  /** Credibility tier from the existing `classifyCredibility` allowlist. */
  tier: CredibilityTier;
  /** T1 (official) / T2 (mainstream) → trusted; T3 (aggregator) / unknown →
   *  unverified. "Trusted" here means "from a curated, editorially-overseen
   *  publisher" — it is NOT a malware/safety verdict on the specific URL. */
  trust: SourceTrust;
}

/**
 * Trust gate over the EXISTING credibility classifier.
 *
 * Reuses `classifyCredibility(domain)` (T1 official / T2 mainstream / T3
 * aggregator) rather than re-deriving tiers, so the allowlist has a single
 * source of truth. T3 (and any domain not on the allowlists, which the
 * classifier already collapses to T3) is treated as UNVERIFIED — the UI must
 * surface that, never hide it.
 *
 * Important honesty note: "trusted" is a statement about the PUBLISHER's
 * editorial credibility, not about the safety of the specific link. Even a
 * T1 link could in theory be a hijacked subdomain; this gate does not claim
 * otherwise. It exists to let the operator weight an aggregator link
 * differently from a WHO bulletin, not to wave a link through unchecked.
 */
export function classifyArticleTrust(article: Pick<CoverageArticle, 'cred' | 'source'>): ArticleTrust {
  // Prefer the article's own classified tier when present (set at ingest by
  // gdeltToArticles); fall back to re-classifying the source/domain string so
  // the gate still works for records that lack a `cred` (defensive).
  const tier: CredibilityTier =
    article.cred ?? classifyCredibility(article.source ?? '');
  const trust: SourceTrust = tier === 1 || tier === 2 ? 'trusted' : 'unverified';
  return { tier, trust };
}
